import type { PackId, PlayerId } from './ids';
import type { CardRegistry, DeckCounts } from './pack';

/**
 * 房间配置（游戏规则）。
 *
 * ⚠️ 评审确认：「就像游戏规则，默认全部使用官方规则，允许房主修改牌堆，自定义各类牌数」。
 * 所以这里的每个字段都是**房主可改**的，默认值来自官方规则书。
 *
 * 这个对象是 `GameState` 的一部分（公开信息），一旦开局就冻结 —— 见 `architecture.md` §3.7。
 */

/** 连击开关。默认只开官方 2022 规则书里的两种。 */
export interface ComboRules {
  /** 两张同名 → 随机抢一张（官方） */
  readonly twoOfAKind: boolean;
  /** 三张同名 → 指定牌名索要（官方） */
  readonly threeOfAKind: boolean;
  /** 五张不同名 → 从弃牌堆任取一张（**游戏规则**，官方 2022 规则书没有这条） */
  readonly fiveDifferent: boolean;
  /** 上面那条能不能把 Catch / Exception 从弃牌堆捞回来 */
  readonly fiveDifferentCanTakeDefuse: boolean;
}

export interface RoomConfig {
  /** 启用了哪些数据包。决定有哪些牌存在（见 `pack.ts`） */
  readonly enabledPacks: readonly PackId[];

  /** 2–5 */
  readonly playerCount: number;

  /** 牌堆构成：牌名 → 放几张。`null` 之外的牌名不会出现在牌堆里。 */
  readonly deck: DeckCounts;

  /**
   * 每人起手发几张**救命牌**（`traits.rescue`，原版包里就是 Catch）。官方 1。
   *
   * ⚠️ 字段名故意用 trait 而不是具体牌名 ——
   * 扩展包可以引入新的救命牌，改数量时不该看起来像只改 Catch。
   */
  readonly openingRescueCount: number;

  /** 每人起手再发几张随机牌（官方 7） */
  readonly openingHandSize: number;

  /**
   * 放进牌堆的**致命牌**数量（`traits.lethal`，原版包里就是 Exception）。
   * `null` = 自动用 `playerCount − 1`（官方）。
   */
  readonly lethalInDeck: number | null;

  /**
   * 扣掉发给玩家的之后，剩余的救命牌里**有几张放回牌堆**。
   * 其余的移出游戏。
   *
   * `null` = 官方行为：**2–3 人局只放回 2 张**，4–5 人局全放回。
   * （这是规则书里最容易漏的一条。）
   */
  readonly extraRescueInDeck: number | null;

  readonly combos: ComboRules;

  /** 出局玩家能否继续打断行动（评审 Q9，默认允许） */
  readonly deadPlayersCanNope: boolean;

  /** 起始玩家怎么定 */
  /** 谁先手：`random` / `host` / 某个具体玩家（房间页点名字设的那个 id） */
  readonly firstPlayer: 'host' | 'random' | PlayerId;
}

// ============================================================
// 官方预设
// ============================================================

/**
 * 官方开局参数（`docs/rules.md` §3.1）。
 *
 * `extraRescueInDeck` = 多余的救命牌里放回牌堆几张。
 * ⚠️ 2–3 人局官方**只放回 2 张**。
 */
export const OFFICIAL_SETUP = {
  2: { lethalInDeck: 1, extraRescueInDeck: 2 },
  3: { lethalInDeck: 2, extraRescueInDeck: 2 },
  4: { lethalInDeck: 3, extraRescueInDeck: 2 },
  5: { lethalInDeck: 4, extraRescueInDeck: 1 },
} as const;

/** 官方规则支持的玩家数 */
export type PlayerCount = keyof typeof OFFICIAL_SETUP;

/** 官方规则支持的玩家数下限 / 上限（由 `OFFICIAL_SETUP` 的键推出） */
const OFFICIAL_COUNTS = Object.keys(OFFICIAL_SETUP).map(Number);
export const MIN_PLAYERS = Math.min(...OFFICIAL_COUNTS);
export const MAX_PLAYERS = Math.max(...OFFICIAL_COUNTS);

/** 每人起手：1 张救命牌 + 7 张随机牌 = 8 张 */
export const OFFICIAL_OPENING = { rescues: 1, others: 7 } as const;

export const DEFAULT_COMBO_RULES: ComboRules = {
  twoOfAKind: true,
  threeOfAKind: true,
  fiveDifferent: false, // 游戏规则，默认关
  fiveDifferentCanTakeDefuse: true,
};

/**
 * 生成一份「官方原版」配置。
 *
 * 牌堆构成直接取注册表里各包的 `defaultCount`（`packs/vanilla` 填的就是官方数量）。
 */
export function officialConfig(
  playerCount: PlayerCount,
  registry: CardRegistry,
): RoomConfig {
  return {
    enabledPacks: registry.packs.map((pack) => pack.id),
    playerCount,
    deck: registry.defaultDeck(),
    openingRescueCount: OFFICIAL_OPENING.rescues,
    openingHandSize: OFFICIAL_OPENING.others,
    // ★ 致命牌默认走**自动**（null = 按人数）；auto-number 控件因此默认开着。
    lethalInDeck: null,
    extraRescueInDeck: null, // null = 走官方逻辑
    combos: { ...DEFAULT_COMBO_RULES },
    deadPlayersCanNope: true,
    firstPlayer: 'random',
  };
}

/** 一局最多能放几张同名牌 —— 防止房主填 999 把房间卡死（`docs/rules.md` §11.2） */
export const MAX_COPIES_PER_TITLE = 20;

// ============================================================
// 改配置的补丁
// ============================================================

/**
 * 一次能改的游戏规则字段。combos 是嵌套的，所以单独收一层 Partial。
 *
 * ⚠️ 它住在 core/ 而不是 ui/：游戏规则字段的语义属于核心配置，注册表里的 patch
 * 也要用它 —— 放在界面层会让功能模块反向依赖界面。
 */
export interface ConfigPatch {
  readonly playerCount?: PlayerCount;
  readonly openingHandSize?: number;
  readonly openingRescueCount?: number;
  readonly lethalInDeck?: number | null;
  readonly extraRescueInDeck?: number | null;
  readonly deadPlayersCanNope?: boolean;
  readonly firstPlayer?: 'host' | 'random' | PlayerId;
  /** 房主选择启用哪些内容包。顺序必须跟本机注册表一致（packRefsOf 的顺序） */
  readonly enabledPacks?: readonly PackId[];
  /** 整副牌堆（导入导出的房间配置会带上它） */
  readonly deck?: DeckCounts;
  readonly combos?: Partial<ComboRules>;
}

/**
 * 官方在 playerCount 人局放几张致命牌。
 *
 * ⚠️ 收 number 而不是 PlayerCount：调用点大多拿着 RoomConfig.playerCount，
 * 它的类型就是 number（配置在编辑过程中可以短暂地是非法值）。
 * 界面显示"官方值"时不该因为一个越界的数字就崩掉。
 */
export function officialLethalFor(playerCount: number): number {
  const table = OFFICIAL_SETUP as Record<number, { lethalInDeck: number } | undefined>;
  return table[playerCount]?.lethalInDeck ?? table[2]?.lethalInDeck ?? 1;
}
